import React, { useEffect, useMemo, useState } from 'react'
import pa from '../ProductActivityPage.module.css'
import styles from './Pallets.module.css'
import st from './Stock.module.css'
import l from './List.module.css'
import { ExportIcon } from './chrome'
import { workbook, XLSX_TYPE } from '../batches/xlsx'
import { fileTask, useTasks } from '../../components/TaskCentre'
import { useHelpPref, usePreference } from '../../lib/preferences'
import { SkeletonTableRows } from '../../components/pending'
import { fmtDay } from './model'
import { StockDrawer } from './StockDrawer'
import {
  HOLD_FILTERS,
  COVERAGE_FAILED_LINE,
  COVERAGE_FAILED_TITLE,
  COVERAGE_NONE_TITLE,
  coverageGap,
  coverageGapLines,
  coverageGapTitle,
  coverageLimitLines,
  distinct,
  filterStock,
  dropUrlParams,
  harvestRange,
  oneOf,
  palletisedText,
  rowShort,
  siteText,
  stockExportNote,
  stockTotal,
  summariseStock,
  summaryTotal,
  urlParam,
  type Coverage,
  type HoldFilter,
  type StockRow,
  type StockSummary,
} from './stock'
import { NoteBubble } from './NoteBubble'
import { openableRow } from '../../lib/openableRow'
import { hasCapability, useAccess } from '../../lib/access'
import { StockTakePanel } from './StockTakePanel'
import type { StockTakeScope } from './stock'

// The Stock list (V4 Stock → Stock; handoff §7n.5): one row per source group,
// the ledger's figures as the API settled them, and the group's drawer.

const COLUMNS: [string, string | undefined, boolean?][] = [
  ['Harvest', "Harvest date: part of the group's identity"],
  ['Site / block', 'Site and block picked from'],
  ['Contractor', 'Picking contractor'],
  ['Variety', undefined],
  ['Box / pack', 'Box or pack type, with its capacity snapshot'],
  ['Lid at picking', 'Lid applied at picking. An order may require a different one: that shows as a lid change'],
  ['/plt', 'Boxes per pallet, snapshotted from the picking job', true],
  ['On hand', 'Received − departed − written off ± stock takes. Palletising does not reduce it', true],
  ['Held', 'Blocked by a hold, on the source or on an unassigned pallet', true],
  ['Reserved', 'Boxes committed to orders', true],
  ['Available', 'On hand − held − reserved', true],
  ['Palletised', 'Planned and confirmed boxes on live pallets: still on hand until they depart'],
]

// The summary view: one row per variety, pack and contractor, opening onto
// its dated source groups.
const SUMMARY_COLUMNS: [string, string | undefined, boolean?][] = [
  ['Variety', undefined],
  ['Box / pack', 'Box or pack type, with its capacity snapshot'],
  ['Lid at picking', 'Lid applied at picking'],
  ['Contractor', 'Picking contractor'],
  ['Harvested', 'Earliest to latest harvest date in this line. Open the row for each date'],
  ['/plt', 'Boxes per pallet, snapshotted from the picking job', true],
  ['On hand', 'Received − departed − written off ± stock takes. Palletising does not reduce it', true],
  ['Held', 'Blocked by a hold, on the source or on an unassigned pallet', true],
  ['Reserved', 'Boxes committed to orders', true],
  ['Available', 'On hand − held − reserved', true],
  ['Palletised', 'Planned and confirmed boxes on live pallets: still on hand until they depart'],
]

export type StockViewMode = 'summary' | 'detail'

// What a Stock take button counts: a summary row's whole line, or one group.
interface Take {
  sourceId: string
  scope: StockTakeScope
  title: string
}
const lineTitle = (r: Pick<StockRow, 'variety' | 'box_type' | 'lid_name' | 'contractor_name'>) =>
  [r.variety, r.box_type, r.lid_name || 'No lid specified', r.contractor_name].filter(Boolean).join(' · ')
const groupTitle = (r: StockRow) => lineTitle(r) + ' · ' + fmtDay(r.harvest_date) + ' · ' + siteText(r)

const HOLD_KEYS = HOLD_FILTERS.map(([v]) => v)

export function StockView({
  user,
  rows,
  coverage = { state: 'checking' },
  onRetryCoverage,
  showingLoading,
  onChanged,
}: {
  user: any
  rows: StockRow[]
  coverage?: Coverage
  onRetryCoverage?: () => void
  showingLoading?: boolean
  onChanged: () => void
}) {
  const [q, setQ] = useState('')
  const [variety, setVariety] = useState('')
  const [contractor, setContractor] = useState('')
  const [hold, setHold] = useState<HoldFilter>(() => oneOf(urlParam('hold'), HOLD_KEYS, ''))
  const [openId, setOpenId] = useState<string | null>(() => urlParam('open'))
  const [toast, setToast] = useState('')
  const [helpOn, toggleHelp] = useHelpPref(user, 'stock')
  const [mode, setMode] = usePreference<StockViewMode>(user, 'stock.view', 'summary')
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const toggleGroup = (key: string) =>
    setExpanded((cur) => {
      const next = new Set(cur)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  const { startTask } = useTasks()
  const mayManage = hasCapability(useAccess(), 'manage_stock_dispatch')
  const [take, setTake] = useState<Take | null>(null)
  const takeCell = (t: Take) =>
    mayManage ? (
      <td className={st.takeCol}>
        <button
          type="button"
          className={l.rowBtn}
          onClick={(e) => {
            e.stopPropagation()
            setTake(t)
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          Stock take
        </button>
      </td>
    ) : null

  // A deep link from the Overview (handoff §7n.1a) sets where this starts.
  useEffect(() => dropUrlParams(['hold', 'open']), [])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3400)
    return () => clearTimeout(t)
  }, [toast])

  const shown = filterStock(rows, { q, variety, contractor, hold })
  const groups = useMemo(() => summariseStock(shown), [shown])
  const summary = mode !== 'detail'
  const cols = summary ? SUMMARY_COLUMNS : COLUMNS
  const span = cols.length + (summary ? 1 : 0) + (mayManage ? 1 : 0)
  const varieties = useMemo(() => distinct(rows.map((r) => r.variety)), [rows])
  const contractors = useMemo(() => distinct(rows.map((r) => r.contractor_name)), [rows])

  const exportRows = () => {
    const out = [
      ['Source', 'Harvest', 'Site', 'Block', 'Contractor', 'Variety', 'Box type', 'Lid at picking', 'Capacity', 'Received', 'On hand', 'Held', 'Reserved', 'Available', 'Planned on pallets', 'Confirmed on pallets', 'To palletise'],
      ...shown.map((r) => [
        r.id,
        r.harvest_date,
        r.site_name,
        r.block_name,
        r.contractor_name,
        r.variety,
        r.box_type,
        r.lid_name || '',
        r.capacity_per_pallet,
        r.received_boxes,
        r.boxes_on_hand,
        r.boxes_held,
        r.boxes_reserved,
        r.boxes_available,
        r.boxes_palletised ?? 0,
        r.boxes_confirmed_on_pallets ?? 0,
        r.boxes_to_palletise ?? 0,
      ]),
    ]
    startTask(fileTask('Export stock', stockExportNote(shown), 'stock.xlsx', XLSX_TYPE, () => workbook([{ name: 'Stock', rows: out }])))
  }

  return (
    <div className={styles.wrap}>
      <div className={l.toolbar}>
        <input
          className={l.search}
          placeholder="Search source, variety, job…"
          aria-label="Search stock"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select className={l.select} aria-label="Variety" value={variety} onChange={(e) => setVariety(e.target.value)}>
          <option value="">All varieties</option>
          {varieties.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
        <select className={l.select} aria-label="Contractor" value={contractor} onChange={(e) => setContractor(e.target.value)}>
          <option value="">All contractors</option>
          {contractors.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
        <select className={l.select} aria-label="Hold state" value={hold} onChange={(e) => setHold(e.target.value as HoldFilter)}>
          {HOLD_FILTERS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <div className={st.seg} role="group" aria-label="Stock view">
          <button type="button" className={summary ? st.segOn : st.segBtn} aria-pressed={summary} onClick={() => setMode('summary')}>
            Summary
          </button>
          <button type="button" className={!summary ? st.segOn : st.segBtn} aria-pressed={!summary} onClick={() => setMode('detail')}>
            By harvest date
          </button>
        </div>
        <button type="button" className={l.helpPill} onClick={toggleHelp} aria-expanded={helpOn}>
          {helpOn ? 'Hide help' : 'How stock is counted'}
        </button>
        {!showingLoading && <span className={l.stats}>{summary ? summaryTotal(groups, shown) : stockTotal(shown)}</span>}
        <button type="button" className={l.exportBtn} onClick={exportRows}>
          <ExportIcon />
          Export current results
        </button>
      </div>

      {helpOn && <StockHelp onClose={toggleHelp} />}

      <CoverageNotice coverage={coverage} onRetry={onRetryCoverage} />

      <div className={l.card}>
        <table className={st.table} style={{ minWidth: '67.5rem' }}>
          <thead>
            <tr>
              {summary && <th className={st.chevCol} aria-label="Open" />}
              {cols.map(([label, tip, num]) => (
                <th key={label} title={tip} className={num ? st.num : undefined}>
                  {label}
                </th>
              ))}
              {mayManage && <th className={st.takeCol} aria-label="Stock take" />}
            </tr>
          </thead>
          {showingLoading ? (
            <SkeletonTableRows cols={span} label="Loading stock" />
          ) : shown.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={span} className={st.empty}>
                  {rows.length === 0
                    ? 'No received stock yet. Stock appears here once packhouse receipts are accepted: there is no manual stock entry.'
                    : 'No stock matches these filters.'}
                </td>
              </tr>
            </tbody>
          ) : summary ? (
            <tbody>
              {groups.map((g) => (
                <SummaryRows key={g.key} g={g} open={expanded.has(g.key)} onToggle={() => toggleGroup(g.key)} onOpen={setOpenId} takeCell={takeCell} />
              ))}
            </tbody>
          ) : (
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} className={st.row} data-testid="stock-row" {...openableRow(() => setOpenId(r.id))}>
                  <td>{fmtDay(r.harvest_date)}</td>
                  <td>{siteText(r)}</td>
                  <td>{r.contractor_name || '—'}</td>
                  <td className={styles.strong}>
                    {r.variety}
                    <NoteBubble notes={r.pallet_notes || []} />
                  </td>
                  <td>{r.box_type}</td>
                  <td className={styles.muted}>{r.lid_name || 'Not specified'}</td>
                  <td className={st.num}>{r.capacity_per_pallet}</td>
                  <Figures r={r} />
                  {takeCell({ sourceId: r.id, scope: 'source', title: groupTitle(r) })}
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
      <div className={st.foot}>
        All stock originates in Dataphyll picking and packhouse receipts. There is no “Add external stock”. Control-only receipts belong in the
        existing receipt and reconciliation flow.
      </div>

      {openId && (
        <StockDrawer
          user={user}
          sourceId={openId}
          onClose={() => setOpenId(null)}
          onChanged={(msg) => {
            if (msg) setToast(msg)
            onChanged()
          }}
        />
      )}
      {take && (
        <StockTakePanel
          user={user}
          sourceId={take.sourceId}
          scope={take.scope}
          title={take.title}
          onCancel={() => setTake(null)}
          onDone={(msg) => {
            setTake(null)
            setToast(msg)
            onChanged()
          }}
        />
      )}
      {toast && (
        <div className={pa.toast} role="status">
          {toast}
        </div>
      )}
    </div>
  )
}

// The ledger's figures: the same five cells on a source group and a summary.
type Figured = Pick<StockRow, 'boxes_on_hand' | 'boxes_held' | 'boxes_reserved' | 'boxes_available' | 'boxes_palletised' | 'boxes_confirmed_on_pallets' | 'boxes_shortfall'>
function Figures({ r }: { r: Figured }) {
  return (
    <>
      <td className={st.num}>{r.boxes_on_hand}</td>
      <td className={`${st.num} ${r.boxes_held > 0 ? st.heldOn : ''}`}>{r.boxes_held}</td>
      <td className={st.num}>{r.boxes_reserved}</td>
      <td className={`${st.num} ${r.boxes_available > 0 ? st.availOn : st.availOff}`}>{r.boxes_available}</td>
      <td className={styles.sub}>
        {palletisedText(r)}
        {(r.boxes_shortfall ?? 0) > 0 && <span className={st.shortLine}>{rowShort(r)}</span>}
      </td>
    </>
  )
}

// A summary row and, when open, its source groups by harvest date, each
// opening the source-group drawer as on the dated view.
function SummaryRows({
  g,
  open,
  onToggle,
  onOpen,
  takeCell,
}: {
  g: StockSummary
  open: boolean
  onToggle: () => void
  onOpen: (id: string) => void
  takeCell: (t: Take) => React.ReactNode
}) {
  return (
    <>
      <tr className={st.row} data-testid="stock-summary-row" aria-expanded={open} {...openableRow(onToggle)}>
        <td className={st.chevCol} aria-hidden>
          <span className={open ? st.chevOpen : st.chev}>›</span>
        </td>
        <td className={styles.strong}>
          {g.variety}
          <NoteBubble notes={g.rows.flatMap((r) => r.pallet_notes || [])} />
        </td>
        <td>{g.box_type}</td>
        <td className={styles.muted}>{g.lid_name || 'Not specified'}</td>
        <td>{g.contractor_name || '—'}</td>
        <td>
          {harvestRange(g)}
          {g.rows.length > 1 && <span className={st.subLine}>{g.rows.length} source groups</span>}
        </td>
        <td className={st.num}>{g.capacity_per_pallet}</td>
        <Figures r={g} />
        {takeCell({ sourceId: g.rows[0].id, scope: 'line', title: lineTitle(g) })}
      </tr>
      {open &&
        g.rows.map((r) => (
          <tr key={r.id} className={`${st.row} ${st.childRow}`} data-testid="stock-row" {...openableRow(() => onOpen(r.id))}>
            <td className={st.chevCol} />
            <td colSpan={4} className={styles.sub}>
              {siteText(r)}
              {r.job_name ? ' · job ' + r.job_name : ''}
              <NoteBubble notes={r.pallet_notes || []} />
            </td>
            <td>{fmtDay(r.harvest_date)}</td>
            <td className={st.num}>{r.capacity_per_pallet}</td>
            <Figures r={r} />
            {takeCell({ sourceId: r.id, scope: 'source', title: groupTitle(r) })}
          </tr>
        ))}
    </>
  )
}

// V4 "How stock is counted", word for word.
// Receipts outside coverage, beside the stock (migrations 115, 116). A failed
// read says so and offers Retry; it is never shown as "none". No recorded
// gaps is shown only where it falls short of the workspace's whole history.
function CoverageNotice({ coverage, onRetry }: { coverage: Coverage; onRetry?: () => void }) {
  if (coverage.state === 'checking') return null
  if (coverage.state === 'failed') {
    return (
      <div className={st.gapBanner} role="alert" data-testid="coverage-unchecked">
        <span className={st.gapTitle}>{COVERAGE_FAILED_TITLE}</span>
        <span className={st.gapLine}>{COVERAGE_FAILED_LINE}</span>
        {onRetry && (
          <span>
            <button type="button" className={pa.ghostBtn} onClick={onRetry} disabled={coverage.retrying}>
              {coverage.retrying ? 'Checking…' : 'Retry'}
            </button>
          </span>
        )}
      </div>
    )
  }
  const gap = coverageGap(coverage.gap)
  if (gap) {
    return (
      <div className={st.gapBanner} role="status" data-testid="coverage-gap">
        <span className={st.gapTitle}>{coverageGapTitle(gap)}</span>
        {coverageGapLines(gap).map((line) => (
          <span key={line} className={st.gapLine}>
            {line}
          </span>
        ))}
      </div>
    )
  }
  const limits = coverageLimitLines(coverage.gap)
  if (!limits.length) return null
  return (
    <div className={st.coverNote} role="status" data-testid="coverage-limits">
      <span className={st.gapTitle}>{COVERAGE_NONE_TITLE}</span>
      {limits.map((line) => (
        <span key={line} className={st.gapLine}>
          {line}
        </span>
      ))}
    </div>
  )
}

function StockHelp({ onClose }: { onClose: () => void }) {
  return (
    <div className={l.help} data-testid="stock-help">
      <div className={l.helpHead}>
        <span className={l.helpTitle}>How stock is counted</span>
        <span className={l.helpLead}>Every box here came from a Dataphyll packhouse receipt. Nothing is entered by hand.</span>
        <button type="button" className={l.helpX} title="Hide help" aria-label="Hide help" onClick={onClose}>
          ×
        </button>
      </div>
      <div className={l.helpItem}>
        <span>Summary, or by harvest date</span>
        <p>
          <strong>Summary</strong> shows one row per variety, box or pack, lid and contractor, adding up every harvest date, site and block. Open a
          row to see its dates. <strong>By harvest date</strong> lists every source group on its own.
        </p>
      </div>
      <div className={l.helpItem}>
        <span>One source group</span>
        <p>
          A picking job’s receipts, split by harvest date, site and block, contractor, variety, box or pack type, capacity and lid at picking. Two
          of those differing makes two rows and they are never merged. An order can require a specific harvest date or lid, and merging would lose
          the ability to honour it.
        </p>
      </div>
      <div className={l.helpItem}>
        <span>Where the numbers come from</span>
        <div className={l.defs}>
          <span>
            <strong>On hand</strong> = received − departed − written off ± stock takes
          </span>
          <span>
            <strong>Held</strong> = boxes blocked by a hold, on the source or on an unassigned pallet
          </span>
          <span>
            <strong>Reserved</strong> = boxes committed to orders
          </span>
          <span>
            <strong>Available</strong> = on hand − held − reserved
          </span>
        </div>
      </div>
      <div className={l.helpItem}>
        <span>Palletising is not leaving</span>
        <p>
          A box stays in stock until a load confirms its departure. The same box can be on a confirmed pallet, reserved against an order and still on
          hand. Palletised and reserved are two readings of the same boxes, never subtracted twice. <strong>To palletise</strong> is the
          unpalletised remainder: on hand − palletised − holds.
        </p>
      </div>
    </div>
  )
}
